import { success } from '@helpers/result-helper'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import { cloneDeep } from 'lodash'
import DisputeFollowUpTaskTypes from '@/constants/disputes/DisputeFollowUpTaskTypes'
import parseJson from '@/utils/parse-json'
import DisputeDetailsResolutionView from '@/models/disputes/responses/disputeDetailsResolutionView'
import DisputeListItem from '@/models/disputes/disputeListItem'
import DisputeTaskListItem from '@/models/disputes/disputeTaskListItem'
import { VuexResponse } from '../../helpers/vuex-action-builder'

const getDefaultState = () => {
  return {
    // Place any new state properties here
    loadingCount: 0,
    crudLoadingCount: 0,
    disputeSubmitLoadingCount: 0,
    disputeTaskLoadingCount: 0,
    disputePages: {},
    allowedAdminIds: [],
    flyout: {
      resolveStatus: null,
      timesheet: {},
      show: false,
      invoiceCenter: {},
      invoiceCenterDateUntil: null,
      task: {
        id: null,
        bookingId: null,
        status: null,
        formUrl: null,
      },
    },
    invoiceCenters: [],
    disputeFollowUpTasks: [],
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,
  getters: {
    isLoading: (state) => state.loadingCount > 0,
    isLoadingCRUD: (state) => state.crudLoadingCount > 0,
    isLoadingDisputeSubmit: (state) => state.disputeSubmitLoadingCount > 0,
    isLoadingDisputeTasks: (state) => state.disputeTaskLoadingCount > 0,
    getAllowedToViewAdminIds: (state) => state.allowedAdminIds,
    activeTimesheet: (state) => state.flyout.timesheet,
    resolveStatus: (state) => state.flyout.resolveStatus,
    showFlyout: (state) => state.flyout.show,
    showResolveContent: (state) =>
      !!state.flyout.timesheet?.bookingId && !state.flyout.task?.id,
    showInvoiceCenterContent: (state) =>
      !!state.flyout.invoiceCenter?.invoiceCenterId,
    showDisputesTaskContent: (state) => !!state.flyout.task?.id,
    invoiceCenters: (state) => state.invoiceCenters,
    invoiceCenter: (state) => (id) =>
      state.invoiceCenters.find(
        (invoiceCenter) => invoiceCenter.invoiceCenterId === id
      ),
    disputeFollowUpTasks: (state) => {
      const completedTasks = sessionStorage.getItem('completedTasks')

      if (!completedTasks) return state.disputeFollowUpTasks

      const parsedCompletedTasks = parseJson(completedTasks, [])

      return state.disputeFollowUpTasks.filter(
        ({ taskId }) => !parsedCompletedTasks?.includes(taskId)
      )
    },
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    START_LOADING_DISPUTE_SUBMIT(state) {
      state.disputeSubmitLoadingCount++
    },
    FINISH_LOADING_DISPUTE_SUBMIT(state) {
      if (state.disputeSubmitLoadingCount > 0) state.disputeSubmitLoadingCount--
    },
    START_LOADING_TASK_TABLE(state) {
      state.disputeTaskLoadingCount++
    },
    FINISH_LOADING_TASK_TABLE(state) {
      if (state.disputeTaskLoadingCount > 0) state.disputeTaskLoadingCount--
    },
    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
    SAVE_DISPUTE_PAGE(state, pagedResponse) {
      state.disputePages[pagedResponse.key] = pagedResponse
    },
    SAVE_ALLOWED_ADMINIDS(state, adminIds) {
      state.allowedAdminIds = adminIds
    },
    SET_FLYOUT(state, { key, value }) {
      state.flyout[key] = value
    },
    RESET_FLYOUT(state) {
      state.flyout = {
        resolveStatus: null,
        timesheet: {},
        show: false,
        invoiceCenter: {},
        bulkOperation: null,
        invoiceCenterDateUntil: null,
        task: {
          id: null,
          formUrl: null,
          bookingId: null,
          status: null,
        },
      }
    },
    SET_STATE(state, { key, value }) {
      state[key] = value
    },
    UPDATE_INVOICE_CENTERS(state, data) {
      const index = state.invoiceCenters.findIndex(
        (ic) => ic.id === data[0].invoiceCenterId
      )

      if (index > -1) {
        state.invoiceCenters.splice(index, 1, {
          ...state.invoiceCenters[index],
          ...data[0],
        })
      }
    },
    SET_FOLLOWUP_TASKS(state, tasks) {
      state.disputeFollowUpTasks = tasks
        ? tasks.map((task) => new DisputeTaskListItem(task))
        : []
    },
  },
  actions: {
    /**
     * @param commit
     * @param state
     * @param disputeReqParams {DisputeListQuery}
     * @return {Promise<*|{data: DisputeListItem, message: string, errors: [], isSuccess: boolean, statusCode: null}>}
     */
    async getDisputes({ commit, dispatch, state }, { params, refresh }) {
      const pageKey = new URLSearchParams(params).toString()

      if (state.disputePages[pageKey] && !refresh) {
        return success(state.disputePages[pageKey])
      }

      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.disputes.getBookingDisputeList(params))
        .withPagination(pageKey)
        .withLoading()
        .withCommit('SAVE_DISPUTE_PAGE')
        .go()
    },

    /**
     * @param commit
     * @param state
     * @param disputeReqParams {DisputeListQuery}
     * @return {Promise<*|{data: Array<DisputeListItem>, message: string, errors: [], isSuccess: boolean, statusCode: null}>}
     */
    async getDisputesV2({ commit, dispatch }, { params }) {
      const processedParams = {
        type: params?.filter?.type || '',
        ids: isNullOrEmptyArray(params?.filter?.ids)
          ? ''
          : params.filter.ids.join(','),
        placementStatuses: isNullOrEmptyArray(params?.placementStatuses)
          ? ''
          : params.placementStatuses.join(','),
      }

      const toDisputeListItem = (item) => new DisputeListItem(item)

      return await new VuexResponse(commit, dispatch)
        .request(() =>
          this.$api.disputes.getBookingDisputeListV2(processedParams)
        )
        .withLoading()
        .transform((data) => data.map(toDisputeListItem))
        .go()
    },

    /**
     * @param commit
     * @param state
     * @param disputeReqParams {DisputeListQuery}
     * @return {Promise<*|{data: DisputeListItem, message: string, errors: [], isSuccess: boolean, statusCode: null}>}
     */
    async getDisputeFollowUpTasks({ commit, dispatch }, { params }) {
      const processedParams = {
        type: params?.filter?.type || '',
        ids: isNullOrEmptyArray(params?.filter?.ids)
          ? ''
          : params.filter.ids.join(','),
        placementStatuses: isNullOrEmptyArray(params?.placementStatuses)
          ? ''
          : params.placementStatuses.join(','),
      }

      return await new VuexResponse(commit, dispatch)
        .request(() =>
          this.$api.disputes.getDisputeFollowUpTasks(processedParams)
        )
        .withLoading()
        .withCommit('SET_FOLLOWUP_TASKS')
        .go()
    },

    async getAllowedViewableAdminIds({ commit, dispatch, state }) {
      if (state.allowedAdminIds.length) {
        return success(state.allowedAdminIds)
      }

      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.disputes.getAllowedViewableAdminIds())
        .withLoading()
        .withCommit('SAVE_ALLOWED_ADMINIDS')
        .go()
    },

    async getDisputeDetailsById({ commit, dispatch }, bookingId) {
      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.disputes.getDisputeByBookingId(bookingId))
        .withFailureToast('Failed to load dispute details')
        .transform((data) => new DisputeDetailsResolutionView(data))
        .go()
    },

    async initialise({ dispatch }) {
      await dispatch('getAllowedViewableAdminIds')
      await dispatch('getInvoiceCenters')
    },

    async submitTimesheets({ commit, dispatch }, payload) {
      if (!payload.length) payload = [payload]
      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.disputes.submitTimesheets(payload))
        .withLoading(
          'START_LOADING_DISPUTE_SUBMIT',
          'FINISH_LOADING_DISPUTE_SUBMIT'
        )
        .withFailureToast('Failed to submit timesheets')
        .withSuccessToast('Timesheets submitted successfully')
        .go()
    },

    async getInvoiceCenters({ commit, dispatch }) {
      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.finance.getInvoiceCenters())
        .withSetState('invoiceCenters')
        .go()
    },
    async getInvoiceCenterDisputesData(
      { commit, dispatch },
      { invoiceCenterId, dateUntil }
    ) {
      return await new VuexResponse(commit, dispatch)
        .request(() =>
          this.$api.disputes.getInvoiceCenterGroupedSummary({
            invoiceCenterId,
            dateUntil,
          })
        )
        .withCommit('UPDATE_INVOICE_CENTERS')
        .go()
    },
    async bulkResolveByInvoiceCenter(
      { state, commit, dispatch },
      { id: invoiceCenterId, operation, dateUntil }
    ) {
      return await new VuexResponse(commit, dispatch)
        .request(() =>
          this.$api.disputes.bulkResolveInvoiceCenter({
            invoiceCenterId,
            operation,
            summary: state.flyout.invoiceCenter,
            dateUntil,
          })
        )
        .withFailureToast('Failed to bulk resolve timesheets')
        .withSuccessToast('Timesheets submitted successfully')
        .go()
    },
    resetInvoiceCenterCounterAfterInsert(
      { commit, getters },
      { invoiceCenterId, operation }
    ) {
      const invoiceCenter = cloneDeep(getters.invoiceCenter(invoiceCenterId))

      switch (operation) {
        case ResolveOperations.ORIGINAL: {
          invoiceCenter.pendingCount = 0
          break
        }
        case ResolveOperations.CANDIDATE: {
          invoiceCenter.candidateCount = 0
          break
        }
        case ResolveOperations.CLIENT: {
          invoiceCenter.clientCount = 0
          break
        }
      }

      commit('UPDATE_INVOICE_CENTERS', [invoiceCenter])
      commit('SET_FLYOUT', {
        key: 'invoiceCenter',
        value: invoiceCenter,
      })
    },
    closeFlyout({ commit }) {
      commit('FINISH_LOADING_DISPUTE_SUBMIT')
      commit('RESET_FLYOUT')
    },
    async goToTaskForm({ state, commit, dispatch, getters }) {
      const { flyout } = state
      const { bookingId } = flyout.timesheet

      // will reset flyout on no task found
      await dispatch('getFollowUpTask', bookingId)

      if (getters.showFlyout) {
        // if flyout hasn't been reset ie: task found
        commit('SET_STATE', {
          key: 'flyout',
          value: {
            ...flyout,
            resolveStatus: null, // Reset resolve status to stop error in computed
          },
        })
        await dispatch('getTaskDetails')
      }
    },
    async getFollowUpTask({ state, commit, dispatch }, bookingId) {
      commit('START_LOADING_DISPUTE_SUBMIT')
      const { isSuccess, data } = await new VuexResponse(commit, dispatch)
        .request(() => this.$api.comments.getAllCommentsForBooking(bookingId))
        .withFailureToast('Failed to get follow up task')
        .go()

      if (!isSuccess) {
        dispatch('closeFlyout')
        return
      }

      const tasks = data.filter(({ isTask }) => isTask)

      if (!tasks.length) {
        dispatch('closeFlyout')
        return
      }

      const task = tasks.find(
        ({ commentReferenceSourceID, type }) =>
          parseInt(commentReferenceSourceID) === bookingId &&
          DisputeFollowUpTaskTypes.includes(type)
      )

      const taskId = task?.id ?? null

      if (!taskId) {
        dispatch('closeFlyout')
        return
      }

      commit('SET_FLYOUT', {
        key: 'task',
        value: {
          ...state.flyout.task,
          id: taskId,
          bookingId,
        },
      })
    },

    async getTaskDetails({ state, commit, dispatch }, taskId = null) {
      taskId = taskId ?? state.flyout.task.id
      const { isSuccess, data, statusCode } = await new VuexResponse(
        commit,
        dispatch
      )
        .request(() => this.$api.tasks.getTaskDetails(taskId))
        .withFailureToast('Failed to get task details')
        .withStatusCode()
        .go()

      if (!isSuccess) {
        dispatch('closeFlyout')
        return
      }

      commit('SET_FLYOUT', {
        key: 'task',
        value: {
          ...state.flyout.task,
          id: taskId,
          formUrl: data?.url ?? null,
          status: statusCode,
        },
      })

      commit('FINISH_LOADING_DISPUTE_SUBMIT')
    },

    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
